import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
    Trash2, Edit2, Plus, Zap, MessageCircle,
    GitBranch, AlertCircle, Layers, X, Workflow, BotMessageSquare
} from 'lucide-react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import AutoReplies from '@Pages/AutoReplies';
import WhatsappFlowService from '@Helpers/URLHelpers/WhatsappFlowService';
import ErrorHandler from '@Root/ErrorHandler';
import './AutoRepliesPage.scss';

const getFormattedData = (date) =>
    new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short',
        day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(date);

function EmptyState({ onCreateFlow }) {
    return (
        <div className="autoRepliesPage__empty">
            <div className="autoRepliesPage__emptyIcon">
                <Workflow size={26} />
            </div>
            <strong>No flows yet</strong>
            <p>Create your first auto-reply flow to start automating conversations.</p>
            <button className="autoRepliesPage__primaryBtn" onClick={onCreateFlow}>
                <Plus size={14} /> Create Flow
            </button>
        </div>
    );
}

const defaultUnsubscribeFlow = {
    builderFlow: {
         nodes: [
             { "id": "s1", "type": "start", "x": 360, "y": 30, "data": {} },
             { "id": "n2", "type": "wait_reply", "x": 320, "y": 160,
                 "data": {
                     "rules": [
                         { "keyword": "hi,hello,hii,hey", "label": "Greeting", "color": "#25D366" },
                         { "keyword": "price,cost,rate", "label": "Pricing", "color": "#1D74F5" },
                         { "keyword": "help,support", "label": "Support", "color": "#7C3AED" },
                         { "keyword": "stop, unsubscribe, not interested", "label": "Unsubscribe", "color": "#DC2626" }
                     ]
                 }
             },
             { "x": 560, "y": 439, "id": "n3", "data": { "text": "" }, "type": "unsubscribe" }
         ],
         edges: [
             { "id": "e1", "source": "s1", "target": "n2" },
             { "id": "e2", "kind": "rule", "source": "n2", "target": "n3", "ruleIdx": 3 }
         ]
    },
    runTimeFlow: {
         "triggerMap": [
             {
                 "triggerType": "keyword",
                 "triggers": [ "stop", "unsubscribe", "not interested" ],
                 "label": "Unsubscribe",
                 "sourceNodeId": "n2",
                 "replyNodeId": "n3",
                 "replyNodeType": "unsubscribe",
                 "waJsonChain": [
                     { "messaging_product": "whatsapp", "to": "{{recipient_phone}}", "type": "unsubscribe", "text": { "preview_url": false, "body": "" } }
                 ]
             }
         ]
    },
}

function FlowCard({ flow, index, onSelect, onEdit, onDelete, onToggleActive }) {
    const triggers = flow?.runTimeFlow?.triggerMap || [];
    const keywords = triggers.filter(t => t.triggerType === 'keyword').flatMap(t => t.triggers);
    const hasFallback = triggers.some(t => t.triggerType === 'fallback');
    const hasButtons = triggers.some(t => t.triggerType === 'button_reply');
    const totalSteps = triggers.reduce((acc, t) => acc + (t.waJsonChain?.length || 0), 0);

    return (
        <div
            className="autoRepliesPage__card"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => onSelect(flow)}>
            <div className="autoRepliesPage__cardHeader">
                <div className="autoRepliesPage__cardHeaderLeft">
                    <h4 className="autoRepliesPage__flowName">
                        {flow?.flowName || 'Untitled Flow'}
                    </h4>
                    <p className="autoRepliesPage__meta">
                        {getFormattedData(flow.createTimeStamp)}
                    </p>
                </div>
                <div className="autoRepliesPage__controls">
                    <button
                        className={`autoRepliesPage__status ${flow.isInUse ? 'active' : ''}`}
                        onClick={(e) => onToggleActive(e, flow.id)}
                        title={flow.isInUse ? 'Click to deactivate' : 'Click to activate'}>
                        <span className="autoRepliesPage__statusDot" />
                        {flow.isInUse ? 'Active' : 'Inactive'}
                    </button>
                    <button
                        className="autoRepliesPage__iconBtn"
                        title="Edit flow"
                        onClick={(e) => { e.stopPropagation(); onEdit(flow); }}>
                        <Edit2 size={13} />
                    </button>
                    <button
                        className="autoRepliesPage__iconBtn delete"
                        title="Delete flow"
                        onClick={(e) => onDelete(e, flow.id)}>
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
            <div className="autoRepliesPage__cardBody">
                <div className="autoRepliesPage__infoRow">
                    <div className="autoRepliesPage__infoItem">
                        <GitBranch size={12} />
                        <span>{triggers.length} Trigger{triggers.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="autoRepliesPage__infoItem">
                        <Layers size={12} />
                        <span>{totalSteps} Step{totalSteps !== 1 ? 's' : ''}</span>
                    </div>
                </div>
                {keywords.length > 0 && (
                    <div className="autoRepliesPage__section">
                        <span className="autoRepliesPage__label">
                            <MessageCircle size={11} /> Keywords
                        </span>
                        <div className="autoRepliesPage__tags">
                            {keywords.slice(0, 4).map((k, i) => (
                                <span key={i} className="autoRepliesPage__tag">{k}</span>
                            ))}
                            {keywords.length > 4 && (
                                <span className="autoRepliesPage__more">+{keywords.length - 4}</span>
                            )}
                        </div>
                    </div>
                )}
                {(hasFallback || hasButtons || keywords.length > 0) && (
                    <div className="autoRepliesPage__badges">
                        {hasFallback && (
                            <span className="autoRepliesPage__badge fallback">
                                <AlertCircle size={11} /> Fallback
                            </span>
                        )}
                        {hasButtons && (
                            <span className="autoRepliesPage__badge button">
                                <Zap size={11} /> Buttons
                            </span>
                        )}
                        {keywords.length > 0 && (
                            <span className="autoRepliesPage__badge keyword">
                                <MessageCircle size={11} /> Keywords
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function CreateFlowModal({ open, onClose, flowName, onFlowNameChange, onCreate, isCreating }) {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && flowName?.trim() && !isCreating) onCreate();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box className="autoRepliesPage__modal">
                <div className="autoRepliesPage__modalHeader">
                    <div className="autoRepliesPage__modalIcon">
                        <Plus size={17} />
                    </div>
                    <div className="autoRepliesPage__modalHeaderText">
                        <h3>Create Flow</h3>
                        <p>Start building your automation</p>
                    </div>
                    <button
                        className="autoRepliesPage__modalClose"
                        onClick={onClose}
                        aria-label="Close">
                        <X size={15} />
                    </button>
                </div>
                <div className="autoRepliesPage__modalBody">
                    <div className="autoRepliesPage__inputGroup">
                        <label htmlFor="ar-flowName">Flow Name</label>
                        <input
                            id="ar-flowName"
                            type="text"
                            value={flowName}
                            onChange={onFlowNameChange}
                            onKeyDown={handleKeyDown}
                            placeholder="e.g. Lead Qualification Flow"
                            autoFocus
                        />
                        {flowName?.trim() && (
                            <span className="autoRepliesPage__inputHint">
                                Press Enter to create
                            </span>
                        )}
                    </div>
                </div>
                <div className="autoRepliesPage__modalFooter">
                    <BorderWhiteButtonV2
                        text="Cancel"
                        onClick={onClose}
                        disabled={isCreating}
                    />
                    <BorderBlueButtonV2
                        text={isCreating ? 'Creating…' : 'Create Flow'}
                        onClick={onCreate}
                        disabled={isCreating || !flowName?.trim()}
                    />
                </div>
            </Box>
        </Modal>
    );
}

export default function AutoRepliesPage() {

    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [flowName, setFlowName] = useState('');
    const [openCreateFlowForm, setOpenCreateFlowForm] = useState(false);
    const [whatsappFlowList, setWhatsappFlowList] = useState([]);
    const [selectedFlow, setSelectedFlow] = useState(null);
    const { currentLeadsId, currentPhoneNumber, whatsappFlowProtection } = useReduxLeads();
    const { currentProjectId } = useReduxProject();
    const whatsappFlowService = new WhatsappFlowService();
    const errorHandler = new ErrorHandler();

    useEffect(() => {
        renderData();
    }, [currentPhoneNumber]);

    const renderData = async () => {
        setLoading(true);
        try {
            let response = await whatsappFlowService.getByLeadsConnectionIdAndPhoneNumberId(
                currentLeadsId, currentPhoneNumber?.id
            );
            if (response.status < 400) {
                response = JSON.parse(response.body);
                const flows = response
                    .map(item => ({
                        ...item,
                        builderFlow: JSON.parse(item.builderFlow),
                        runTimeFlow: JSON.parse(item.runTimeFlow),
                    }))
                    .sort((a, b) => b.createTimeStamp - a.createTimeStamp);
                setWhatsappFlowList(flows);
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const handleDeleteRule = async (e, ruleId) => {
        e.stopPropagation();
        try {
            const response = await whatsappFlowService.deleteWhatsappFlow({ id: ruleId });
            if (response.status < 400) {
                SnackbarManager.show(response.message, 'success');
                setWhatsappFlowList(prev => prev.filter(r => r.id !== ruleId));
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) { errorHandler.handleError(err); }
    };

    const handleActiveWhatsappFlow = async (e, ruleId) => {
        setCursor('wait');
        e.stopPropagation();
        try {
            const response = await whatsappFlowService.activeWhatsappFlow({ id: ruleId });
            if (response.status < 400) {
                SnackbarManager.show(response.message, 'success');
                await renderData();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const handleCreateFlow = async () => {
        setCursor('wait');
        try {
            const body = {
                flowName,
                projectId: currentProjectId,
                leadsConnectionId: currentLeadsId,
                phoneNumberId: currentPhoneNumber?.id,
                ...(whatsappFlowProtection ? defaultUnsubscribeFlow : { builderFlow: {}, runTimeFlow: {},})
            };
            let response = await whatsappFlowService.createWhatsappFlow(body);
            if (response.status < 400) {
                SnackbarManager.show(response.message, 'success');
                response = JSON.parse(response.body);
                const flow = {
                    ...response,
                    builderFlow: JSON.parse(response.builderFlow),
                    runTimeFlow: JSON.parse(response.runTimeFlow),
                };
                setWhatsappFlowList(prev => [flow, ...prev]);
                toggleOpenCreateFlowForm();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) { errorHandler.handleError(err); }
        setFlowName('');
        setCursor('auto');
    };

    const disableAllWhatsappFlow = async () => {
        try {
            const response = await whatsappFlowService.disableAllWhatsappFlow({
                projectId: currentProjectId,
                leadsConnectionId: currentLeadsId,
            });
            if (response.status < 400) renderData();
        } catch (err) { errorHandler.handleError(err); }
    };

    const handleFlowNameChange = (e) => setFlowName(DOMPurify.sanitize(e.target.value));
    const toggleOpenCreateFlowForm = () => setOpenCreateFlowForm(prev => !prev);

    if (selectedFlow) {
        return (
            <AutoReplies
                whatsappFlow={selectedFlow}
                reloadData={renderData}
                onClose={() => setSelectedFlow(null)}
            />
        );
    }

    const activeCount = whatsappFlowList.filter(f => f.isInUse).length;

    return (
        <>
            <div className="autoRepliesPage" style={{cursor: cursor}}>
                <div className="autoRepliesPage__header">
                    <div className='flex-row-center-0p5rem-gap'>
                        <BotMessageSquare size={20}/>
                        <h2 className="autoRepliesPage__title">Auto Replies</h2>
                    </div>
                    <div className="autoRepliesPage__actions">
                        <button
                            className="autoRepliesPage__primaryBtn"
                            onClick={toggleOpenCreateFlowForm}>
                            <Plus size={14} /> Create Flow
                        </button>
                        {whatsappFlowList.length > 0 && (
                            <button
                                className="autoRepliesPage__dangerBtn"
                                onClick={disableAllWhatsappFlow}>
                                Disable All
                            </button>
                        )}
                    </div>
                </div>
                <div className="autoRepliesPage__grid">
                    {loading && whatsappFlowList.length === 0 ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="autoRepliesPage__skeleton" />
                        ))
                    ) : whatsappFlowList.length === 0 ? (
                        <EmptyState onCreateFlow={toggleOpenCreateFlowForm} />
                    ) : (
                        whatsappFlowList.map((flow, index) => (
                            <FlowCard
                                key={flow.id}
                                flow={flow}
                                index={index}
                                onSelect={setSelectedFlow}
                                onEdit={setSelectedFlow}
                                onDelete={handleDeleteRule}
                                onToggleActive={handleActiveWhatsappFlow}
                            />
                        ))
                    )}
                </div>
            </div>
            <CreateFlowModal
                open={openCreateFlowForm}
                onClose={toggleOpenCreateFlowForm}
                flowName={flowName}
                onFlowNameChange={handleFlowNameChange}
                onCreate={handleCreateFlow}
                isCreating={cursor === 'wait'}
            />
        </>
    );
}